import { Component, EventEmitter, Input, Output, OnInit, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { GroupMenu } from '../../../../../core/models/system-settings/group-menus.model';
import { ApiSystemSettingsService } from '../../../../../core/services/api/system-settings/api-system-settings.service';
import { MapperGroupMenusService } from '../../../../../core/mappers/system-settings/mapper-group-menus.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-group-menu',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-group-menu.component.html',
  styleUrl: './modal-group-menu.component.css'
})
export class ModalGroupMenuComponent implements OnInit {
  @Input() data: GroupMenu | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  formData: Partial<GroupMenu> = {
    id: 0,
    name: '',
    number: 1,
    status: true
  };

  isLoading = signal<boolean>(false);

  // ─── Dependencies ───
  private toast = inject(ToastService);
  private apiService = inject(ApiSystemSettingsService);
  private mapper = inject(MapperGroupMenusService);
  private cookieService = inject(CookieService);
  private router = inject(Router);
  private permissionService = inject(MenuPermissionService);

  // ================= Normal Functions =================

  constructor() { }

  ngOnInit() {
    if (this.data) {
      this.formData = { ...this.data };
    }
  }

  /**
   * Closes the modal overlay.
   */
  onClose() {
    this.close.emit();
  }

  /**
   * Validates form and makes API call to create or update the group menu item.
   */
  onSave() {
    if (!this.formData.name || this.formData.number === undefined) {
      this.toast.error('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    this.isLoading.set(true);

    // Extract user ID from cookie
    const userIdStr = this.cookieService.get('user_id');
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;

    // Get menuId from the current route
    const currentPath = this.router.url;
    const menuId = this.permissionService.getMenuId(currentPath) || 15; // Fallback to 15 if not found

    const payload = this.mapper.mapGroupMenuToPayload(this.formData as GroupMenu, userId, menuId);

    if (this.formData.id) {
      this.apiService.updateGroupMenu(payload, this.formData.id).subscribe({
        next: (res: any) => {
          console.log("update data : ", res, JSON.stringify(res));
          if (res?.isSuccess) {
            this.toast.success('แก้ไขข้อมูลสำเร็จ');
            this.saveSuccess.emit();
            this.close.emit();
          } else {
            this.toast.error(res?.message || 'แก้ไขข้อมูลไม่สำเร็จ');
          }
          this.isLoading.set(false);
        },
        error: (err: any) => {
          console.error(err);
          this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
          this.isLoading.set(false);
        }
      });
    } else {
      this.apiService.createGroupMenu(payload).subscribe({
        next: (res: any) => {
          console.log("create data : ", res, JSON.stringify(res));
          if (res?.isSuccess) {
            this.toast.success('สร้างข้อมูลสำเร็จ');
            this.saveSuccess.emit();
            this.close.emit();
          } else {
            this.toast.error(res?.message || 'สร้างข้อมูลไม่สำเร็จ');
          }
          this.isLoading.set(false);
        },
        error: (err: any) => {
          console.error(err);
          this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
          this.isLoading.set(false);
        }
      });
    }
  }

  get canShowActivityHistory(): boolean {
    return !!this.formData.id && this.formData.id > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
